import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { WorkSchedule, Employee } from '../../types/employee';
import {
  resolvePayType,
  isDailyWorkerPay,
  isBusinessServicePay,
} from '../../types/employee';
import { TimeRange } from '../../types/workTimeTemplate';
import { useEmployeeActions, useEmployeeData, useWorkScheduleData } from '../../hooks';
import { useAccountBookStore } from '../../store/accountBookStore';
import TemplateSelector from './TemplateSelector';
import EmployeePayPreviewCard from './EmployeePayPreviewCard';
import { getMinimumWageWarning, MINIMUM_WAGE_2025 } from '../../utils/wageValidation';

interface WorkScheduleFormProps {
  open: boolean;
  onClose: () => void;
  schedule?: WorkSchedule | null;
  selectedDate?: Date;
}

const WorkScheduleForm: React.FC<WorkScheduleFormProps> = ({
  open,
  onClose,
  schedule,
  selectedDate
}) => {
  const { createSchedule, updateSchedule, loading } = useEmployeeActions();
  const { employees } = useEmployeeData();
  const scheduleMonth = selectedDate ?? new Date();
  const { schedules } = useWorkScheduleData(scheduleMonth);
  const currentAccountBook = useAccountBookStore((state) => state.currentAccountBook);
  const userId = useAccountBookStore((state) => state.userId);
  
  // 퇴직자가 아닌 직원만 필터링
  const activeEmployees = employees.filter(emp => !emp.isRetired);
  
  const initialDate = selectedDate ? selectedDate : new Date();
  const initialDateStr = initialDate.toISOString().split('T')[0];

  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateHourlyRate, setTemplateHourlyRate] = useState<number>(0);

  const [formData, setFormData] = useState<Partial<WorkSchedule>>({
    employee_id: '',
    date: initialDateStr,
    start_time: `${initialDateStr}T09:00`,
    end_time: `${initialDateStr}T18:00`,
    break_time: '1시간',
  });

  // 시간 선택을 위한 별도 상태
  const [startHour, setStartHour] = useState('09');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('18');
  const [endMinute, setEndMinute] = useState('00');

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (schedule) {
      const startDate = schedule.start_time ? new Date(schedule.start_time) : null;
      const endDate = schedule.end_time ? new Date(schedule.end_time) : null;
      
      // 템플릿 기반 스케줄인지 확인
      const isTemplateBased = !!(schedule.templateId || schedule.workTime1 || schedule.workTime2);
      setUseTemplate(isTemplateBased);
      
      if (isTemplateBased && schedule.templateId) {
        // 템플릿 기반 스케줄
        setSelectedTemplate({ id: schedule.templateId });
        // 스케줄에 저장된 시급이 있으면 사용, 없으면 직원의 시급 사용
        if (schedule.hourlyRate) {
          setTemplateHourlyRate(schedule.hourlyRate);
        } else if (schedule.employee_id) {
          const emp = employees.find(e => e.id === schedule.employee_id);
          if (emp && emp.isHourly) {
            setTemplateHourlyRate(emp.hourlyRate);
          }
        }
      }
      
      setFormData({
        ...schedule,
        date: schedule.date ? new Date(schedule.date).toISOString().split('T')[0] : '',
        start_time: schedule.start_time ? new Date(schedule.start_time).toISOString().slice(0, 16) : '',
        end_time: schedule.end_time ? new Date(schedule.end_time).toISOString().slice(0, 16) : '',
      });
      
      if (startDate) {
        setStartHour(startDate.getHours().toString().padStart(2, '0'));
        setStartMinute((Math.round(startDate.getMinutes() / 10) * 10).toString().padStart(2, '0'));
      }
      if (endDate) {
        setEndHour(endDate.getHours().toString().padStart(2, '0'));
        setEndMinute((Math.round(endDate.getMinutes() / 10) * 10).toString().padStart(2, '0'));
      }
    } else {
      const baseDate = selectedDate ? selectedDate : new Date();
      const baseDateStr = baseDate.toISOString().split('T')[0];
      setFormData({
        employee_id: '',
        date: baseDateStr,
        start_time: `${baseDateStr}T09:00`,
        end_time: `${baseDateStr}T18:00`,
        break_time: '1시간',
      });
      setStartHour('09');
      setStartMinute('00');
      setEndHour('18');
      setEndMinute('00');
      setUseTemplate(false);
      setSelectedTemplate(null);
      setTemplateHourlyRate(0);
    }
    setErrors({});
  }, [schedule, selectedDate, open, employees]);

  // 템플릿 선택 시 시간 범위 자동 설정
  useEffect(() => {
    if (useTemplate && selectedTemplate && selectedTemplate.timeRanges) {
      const dateStr = formData.date || initialDateStr;
      const baseDate = new Date(dateStr);
      
      // workTime1 설정
      if (selectedTemplate.timeRanges[0]) {
        const range1 = selectedTemplate.timeRanges[0];
        const start1 = new Date(range1.start);
        const end1 = new Date(range1.end);
        start1.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        end1.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        
        setFormData(prev => ({
          ...prev,
          workTime1: { start: start1, end: end1 }
        }));
      }
      
      // workTime2 설정
      if (selectedTemplate.timeRanges[1]) {
        const range2 = selectedTemplate.timeRanges[1];
        const start2 = new Date(range2.start);
        const end2 = new Date(range2.end);
        start2.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        end2.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        
        setFormData(prev => ({
          ...prev,
          workTime2: { start: start2, end: end2 }
        }));
      }
      
      // 휴게시간 설정
      if (selectedTemplate.breakTime) {
        setFormData(prev => ({
          ...prev,
          break_time: selectedTemplate.breakTime
        }));
      }
    }
  }, [selectedTemplate, useTemplate, formData.date, initialDateStr]);

  const selectedEmployee = activeEmployees.find(emp => emp.id === formData.employee_id);
  const selectedPayType = selectedEmployee
    ? resolvePayType(selectedEmployee.payType, selectedEmployee.isHourly)
    : null;
  const isNonRegularSchedule =
    !!selectedEmployee &&
    (isDailyWorkerPay(selectedEmployee) || isBusinessServicePay(selectedEmployee));

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = '근무일을 선택해주세요.';
    }

    if (!formData.employee_id) {
      newErrors.employee_id = '직원을 선택해주세요.';
    }

    if (isNonRegularSchedule) {
      if (useTemplate) {
        newErrors.template = '일당·용역 직원은 커스텀 근무일 등록만 가능합니다.';
      }
      const dateStr = formData.date?.split('T')[0];
      const duplicate = schedules.some((s) => {
        if (schedule && s.id === schedule.id) return false;
        const sDate = typeof s.date === 'string' ? s.date.split('T')[0] : s.date;
        return s.employee_id === formData.employee_id && sDate === dateStr;
      });
      if (duplicate) {
        newErrors.date = '선택한 날짜에 이미 근무 등록이 있어요.';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    if (useTemplate) {
      // 템플릿 기반 스케줄 검증
      if (!selectedTemplate) {
        newErrors.template = '템플릿을 선택해주세요.';
      }
      
      if (!formData.employee_id) {
        newErrors.employee_id = '직원을 선택해주세요.';
      }
      
      // 템플릿 모드에서는 직원 선택 시 시급이 자동으로 설정되므로 별도 검증 불필요
      // 직원의 시급은 자동으로 사용됨
      
      if (!formData.workTime1 && !formData.workTime2) {
        newErrors.workTime = '근무 시간이 설정되지 않았습니다.';
      }
    } else {
      // 커스텀 스케줄 검증
      if (!formData.employee_id) {
        newErrors.employee_id = '직원을 선택해주세요.';
      }

      if (!formData.start_time) {
        newErrors.start_time = '출근 시간을 입력해주세요.';
      }

      if (!formData.end_time) {
        newErrors.end_time = '퇴근 시간을 입력해주세요.';
      }

      if (formData.start_time && formData.end_time) {
        const startTime = new Date(formData.start_time);
        const endTime = new Date(formData.end_time);
        
        if (endTime <= startTime) {
          newErrors.end_time = '퇴근 시간은 출근 시간보다 늦어야 합니다.';
        }
      }
    }

    // 템플릿 모드가 아닐 때만 휴게시간 검증 (템플릿 모드는 템플릿 데이터에 휴게시간이 있음)
    if (!useTemplate && !formData.break_time) {
      newErrors.break_time = '휴게시간을 선택해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!currentAccountBook) {
      alert('가계부가 선택되지 않았습니다.');
      return;
    }

    try {
      let scheduleData: any = {
        date: formData.date, // 'YYYY-MM-DD' 형식
        account_number: currentAccountBook.accountNumber,
      };

      if (isNonRegularSchedule && formData.date && formData.employee_id) {
        const dateStr = formData.date.split('T')[0];
        scheduleData = {
          date: dateStr,
          employee_id: formData.employee_id,
          account_number: currentAccountBook.accountNumber,
          start_time: `${dateStr}T09:00:00`,
          end_time: `${dateStr}T18:00:00`,
          break_time: '1시간',
        };
      } else if (useTemplate && selectedTemplate) {
        // 템플릿 기반 스케줄
        scheduleData.templateId = selectedTemplate.id;
        scheduleData.workTime1 = formData.workTime1;
        scheduleData.workTime2 = formData.workTime2;
        
        // 템플릿 데이터에서 휴게시간 사용
        scheduleData.break_time = selectedTemplate.breakTime || formData.break_time || '1시간';
        
        // 직원 선택 시 해당 직원의 시급 자동 사용 (커스텀 모드와 동일)
        if (formData.employee_id && selectedEmployee) {
          scheduleData.employee_id = formData.employee_id;
          // 직원이 시급제인 경우 해당 직원의 시급 사용
          scheduleData.hourlyRate = selectedEmployee.isHourly ? selectedEmployee.hourlyRate : 0;
        }
      } else {
        // 커스텀 스케줄
        scheduleData.employee_id = formData.employee_id;
        scheduleData.start_time = formData.start_time; // ISO string
        scheduleData.end_time = formData.end_time; // ISO string
        scheduleData.break_time = formData.break_time || '1시간';
      }

      if (schedule) {
        await updateSchedule(schedule.id, scheduleData);
      } else {
        await createSchedule(scheduleData);
      }

      // createSchedule에서 중복 체크 시 alert가 표시되고 조기 종료되므로
      // 여기서는 성공적으로 완료된 경우에만 onClose 호출
      // (중복 체크는 createSchedule 내부에서 처리됨)
      onClose();
    } catch (error: any) {
      console.error('근무 일정 저장 실패:', error);
      const errorMessage = error?.message || '근무 일정 저장에 실패했습니다.';
      alert(errorMessage);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 에러 메시지 제거
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 시간 변경 시 datetime-local 형식으로 변환
  const updateTimeField = (hour: string, minute: string, field: 'start_time' | 'end_time') => {
    if (formData.date) {
      const timeString = `${formData.date}T${hour}:${minute}`;
      setFormData(prev => ({
        ...prev,
        [field]: timeString
      }));
    }
  };

  // 10분 단위 분 옵션 생성
  const minuteOptions = ['00', '10', '20', '30', '40', '50'];
  
  // 시간 옵션 생성 (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: 'var(--fg-strong)', fontSize: '1.25rem' }}>
        {isNonRegularSchedule
          ? schedule
            ? '근무일 수정'
            : '근무일 등록'
          : schedule
            ? '근무 일정 수정'
            : '새 근무 일정 추가'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* 기본 정보 */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'var(--fg-secondary)' }}>
            근무 일정 정보
          </Typography>
          
          {/* 템플릿 사용 여부 — 정규 직원만 */}
          {!isNonRegularSchedule && (
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={useTemplate ? 'template' : 'custom'}
                onChange={(e) => {
                  const useTemp = e.target.value === 'template';
                  setUseTemplate(useTemp);
                  if (!useTemp) {
                    setSelectedTemplate(null);
                    setTemplateHourlyRate(0);
                  }
                }}
              >
                <FormControlLabel value="custom" control={<Radio />} label="커스텀 스케줄" />
                <FormControlLabel value="template" control={<Radio />} label="템플릿 사용" />
              </RadioGroup>
            </FormControl>
          </Box>
          )}

          {isNonRegularSchedule && (
            <Alert severity="info" sx={{ mb: 2 }}>
              일당·용역 직원은 하루씩 등록해주세요. 근무 시간은 09:00~18:00으로 저장됩니다.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* 직원 선택 (템플릿 모드와 커스텀 모드 모두 공통) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.employee_id}>
                <InputLabel>직원</InputLabel>
                <Select
                  value={formData.employee_id || ''}
                  onChange={(e) => {
                    const employeeId = e.target.value;
                    handleInputChange('employee_id', employeeId);
                    
                    // 템플릿 모드에서 직원 선택 시 해당 직원의 시급 자동 설정
                    if (useTemplate && employeeId) {
                      const selectedEmp = activeEmployees.find(emp => emp.id === employeeId);
                      if (selectedEmp && selectedEmp.isHourly) {
                        setTemplateHourlyRate(selectedEmp.hourlyRate);
                      }
                    }
                  }}
                  label="직원"
                >
                  {activeEmployees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.employee_id && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.employee_id}
                </Typography>
              )}
            </Grid>
            
            {useTemplate && !isNonRegularSchedule ? (
              <>
                {/* 템플릿 선택 */}
                {userId && (
                  <Grid item xs={12}>
                    <TemplateSelector
                      selectedTemplateId={selectedTemplate?.id}
                      onSelect={(template) => {
                        setSelectedTemplate(template);
                        if (template && template.timeRanges) {
                          // 템플릿 선택 시 시간 범위는 useEffect에서 자동 설정됨
                        }
                      }}
                      disabled={false}
                      userId={userId}
                    />
                    {errors.template && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.template}
                      </Typography>
                    )}
                  </Grid>
                )}
                
                {errors.workTime && (
                  <Grid item xs={12}>
                    <Alert severity="error">{errors.workTime}</Alert>
                  </Grid>
                )}
              </>
            ) : (
              // 커스텀 모드에서는 이미 공통 직원 선택 필드가 있으므로 별도 필드 없음
              null
            )}
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="근무일"
                type="date"
                value={formData.date || ''}
                onChange={(e) => handleInputChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!errors.date}
                helperText={errors.date}
                required
              />
            </Grid>
            
            {!useTemplate && !isNonRegularSchedule && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    출근 시간
                  </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 80 }}>
                  <InputLabel>시간</InputLabel>
                  <Select
                    value={startHour}
                    onChange={(e) => {
                      setStartHour(e.target.value);
                      updateTimeField(e.target.value, startMinute, 'start_time');
                    }}
                    label="시간"
                  >
                    {hourOptions.map(hour => (
                      <MenuItem key={hour} value={hour}>{hour}시</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography>:</Typography>
                <FormControl sx={{ minWidth: 80 }}>
                  <InputLabel>분</InputLabel>
                  <Select
                    value={startMinute}
                    onChange={(e) => {
                      setStartMinute(e.target.value);
                      updateTimeField(startHour, e.target.value, 'start_time');
                    }}
                    label="분"
                  >
                    {minuteOptions.map(minute => (
                      <MenuItem key={minute} value={minute}>{minute}분</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {errors.start_time && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.start_time}
                </Typography>
              )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    퇴근 시간
                  </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 80 }}>
                  <InputLabel>시간</InputLabel>
                  <Select
                    value={endHour}
                    onChange={(e) => {
                      setEndHour(e.target.value);
                      updateTimeField(e.target.value, endMinute, 'end_time');
                    }}
                    label="시간"
                  >
                    {hourOptions.map(hour => (
                      <MenuItem key={hour} value={hour}>{hour}시</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography>:</Typography>
                <FormControl sx={{ minWidth: 80 }}>
                  <InputLabel>분</InputLabel>
                  <Select
                    value={endMinute}
                    onChange={(e) => {
                      setEndMinute(e.target.value);
                      updateTimeField(endHour, e.target.value, 'end_time');
                    }}
                    label="분"
                  >
                    {minuteOptions.map(minute => (
                      <MenuItem key={minute} value={minute}>{minute}분</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {errors.end_time && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.end_time}
                </Typography>
              )}
                </Grid>
              </>
            )}
            
            {/* 템플릿 모드가 아닐 때만 휴게시간 입력 표시 (템플릿 모드는 템플릿 데이터에 휴게시간이 있음) */}
            {!useTemplate && !isNonRegularSchedule && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.break_time}>
                  <InputLabel>휴게시간</InputLabel>
                  <Select
                    value={formData.break_time || '1시간'}
                    onChange={(e) => handleInputChange('break_time', e.target.value)}
                    label="휴게시간"
                  >
                    <MenuItem value="0분">0분</MenuItem>
                    <MenuItem value="30분">30분</MenuItem>
                    <MenuItem value="1시간">1시간</MenuItem>
                    <MenuItem value="1시간 30분">1시간 30분</MenuItem>
                    <MenuItem value="2시간">2시간</MenuItem>
                  </Select>
                </FormControl>
                {errors.break_time && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.break_time}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* 일당·용역 미리보기 */}
          {isNonRegularSchedule && selectedEmployee && selectedPayType && (selectedEmployee.dailyRate ?? 0) > 0 && (
            <EmployeePayPreviewCard
              name={selectedEmployee.name}
              payType={selectedPayType}
              grossAmount={selectedEmployee.dailyRate ?? 0}
              employee={selectedEmployee}
              insuranceWorkDays={1}
            />
          )}

          {/* 근무 시간 미리보기 — 정규 직원 */}
          {!isNonRegularSchedule && formData.start_time && formData.end_time && selectedEmployee && (
            <>
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'var(--fg-secondary)' }}>
                근무 시간 미리보기
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                background: 'var(--bg-elev-1)', 
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      직원명
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedEmployee.name}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      총 근무 시간
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {(() => {
                        const startTime = new Date(formData.start_time!);
                        const endTime = new Date(formData.end_time!);
                        let workMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
                        
                        // 휴게시간 차감
                        const breakTime = formData.break_time;
                        let breakMinutes = 0;
                        if (breakTime === '0분') breakMinutes = 0;
                        else if (breakTime === '30분') breakMinutes = 30;
                        else if (breakTime === '1시간') breakMinutes = 60;
                        else if (breakTime === '1시간 30분') breakMinutes = 90;
                        else if (breakTime === '2시간') breakMinutes = 120;
                        
                        workMinutes -= breakMinutes;
                        const hours = Math.floor(workMinutes / 60);
                        const minutes = Math.round(workMinutes % 60);
                        
                        return `${hours}시간 ${minutes}분`;
                      })()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      예상 급여 (시급제인 경우)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedEmployee.isHourly ? (() => {
                        const startTime = new Date(formData.start_time!);
                        const endTime = new Date(formData.end_time!);
                        let workMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
                        
                        const breakTime = formData.break_time;
                        let breakMinutes = 0;
                        if (breakTime === '0분') breakMinutes = 0;
                        else if (breakTime === '30분') breakMinutes = 30;
                        else if (breakTime === '1시간') breakMinutes = 60;
                        else if (breakTime === '1시간 30분') breakMinutes = 90;
                        else if (breakTime === '2시간') breakMinutes = 120;
                        
                        workMinutes -= breakMinutes;
                        const hours = workMinutes / 60;
                        const pay = hours * selectedEmployee.hourlyRate;
                        
                        return `${Math.round(pay).toLocaleString()}`;
                      })() : '월급제'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            background: 'var(--primary)',
            '&:hover': {
              background: 'var(--primary-hover)',
            },
          }}
        >
          {loading ? '저장 중...' : schedule ? '수정' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkScheduleForm;
